<div class="info-section-card products-section-card" (click)="$event.stopPropagation()">
  <!-- Header / Action Summary -->
  <div class="flex flex-row items-center justify-between w-full gap-4 pb-5 border-b border-solid border-slate-150 dark:border-slate-800/80 mb-6">
    <div class="flex-1 min-w-0">
      <div class="flex items-center gap-2 mb-1.5 flex-wrap">
        <span class="text-xs font-bold uppercase tracking-widest reference-label">{{ 'reference' | translate }}</span>
        <span class="px-2.5 py-0.5 rounded-full text-xs font-mono font-bold reference-badge">
          {{ quotation.reference ? quotation.reference : '--' }}
        </span>
      </div>
      <h1 class="text-base md:text-lg font-bold tracking-tight leading-tight m-0 text-ellipsis overflow-hidden text-slate-900 dark:text-white">
        {{ quotation.erpId }} - {{ quotation.description ? quotation.description : '--' }}
      </h1>
    </div>

    @if (dialogRef) {
      <button mat-icon-button (click)="dialogRef.close()" [matTooltip]="'common.close' | translate" class="shrink-0">
        <mat-icon>close</mat-icon>
      </button>
    }
  </div>

  <div class="flex flex-col gap-8">
    <!-- General Details Section -->
    <div>
      <div class="section-header">
        <div class="section-badge-icon badge-blue-icon">
          <mat-icon>analytics</mat-icon>
        </div>
        <h2>{{ 'general' | translate }}</h2>
      </div>

      <div class="info-grid">
        <!-- Quotation ERP ID -->
        <div class="info-tile">
          <div class="tile-badge badge-blue">
            <mat-icon>badge</mat-icon>
          </div>
          <div class="info-content">
            <span class="tile-label">{{ 'quotation.id' | translate }}</span>
            <span class="tile-value">{{ quotation.erpId ? quotation.erpId : '--' }}</span>
          </div>
        </div>

        <!-- Version -->
        <div class="info-tile">
          <div class="tile-badge badge-purple">
            <mat-icon>history</mat-icon>
          </div>
          <div class="info-content">
            <span class="tile-label">{{ 'version' | translate }}</span>
            <span class="tile-value">v{{ quotation.version }}</span>
          </div>
        </div>

        <!-- Transaction ID -->
        <div class="info-tile">
          <div class="tile-badge badge-rose">
            <mat-icon>receipt_long</mat-icon>
          </div>
          <div class="info-content">
            <span class="tile-label">{{ 'ORDERS.TRANSACTION_ID' | translate }}</span>
            @if (quotation.transactionId) {
              <span class="tile-value cursor-pointer underline text-indigo-600 dark:text-indigo-400 font-bold flex items-center gap-1" (click)="goToOrder()">
                {{ quotation.transactionId }}
                <mat-icon style="font-size: 14px; width: 14px; height: 14px;">open_in_new</mat-icon>
              </span>
            } @else {
              <span class="tile-value">--</span>
            }
          </div>
        </div>

        <!-- Commercial -->
        <div class="info-tile">
          <div class="tile-badge badge-teal">
            <mat-icon>support_agent</mat-icon>
          </div>
          <div class="info-content">
            <span class="tile-label">{{ 'commercial' | translate }}</span>
            <span class="tile-value">{{ quotation.sales?.name ? quotation.sales?.name : '--' }}</span>
          </div>
        </div>

        <!-- Calculator -->
        <div class="info-tile">
          <div class="tile-badge badge-indigo">
            <mat-icon>calculate</mat-icon>
          </div>
          <div class="info-content">
            <span class="tile-label">{{ 'calculator' | translate }}</span>
            <span class="tile-value">{{ quotation.calculator?.name ? quotation.calculator?.name : '--' }}</span>
          </div>
        </div>

        <!-- Quotation Date -->
        @if (quotation.date) {
          <div class="info-tile">
            <div class="tile-badge badge-emerald">
              <mat-icon>calendar_today</mat-icon>
            </div>
            <div class="info-content">
              <span class="tile-label">{{ 'quotation.date' | translate }}</span>
              <span class="tile-value">{{ quotation.date | date:'shortDate' : '' : translate.currentLang }}</span>
            </div>
          </div>
        }
      </div>
    </div>

    <mat-divider class="opacity-40"></mat-divider>

    <!-- Customer Section -->
    <div>
      <div class="section-header">
        <div class="section-badge-icon badge-rose-icon">
          <mat-icon>business</mat-icon>
        </div>
        <h2>{{ 'customer' | translate }}</h2>
      </div>

      <div class="info-grid">
        <!-- Company -->
        <div class="info-tile">
          <div class="tile-badge badge-rose">
            <mat-icon>business</mat-icon>
          </div>
          <div class="info-content">
            <span class="tile-label">{{ 'company' | translate }}</span>
            <span class="tile-value font-bold">{{ quotation.company ? quotation.company : '--' }}</span>
          </div>
        </div>

        <!-- Contact Name -->
        <div class="info-tile">
          <div class="tile-badge badge-amber">
            <mat-icon>person</mat-icon>
          </div>
          <div class="info-content">
            <span class="tile-label">{{ 'contact' | translate }}</span>
            <span class="tile-value">{{ quotation.contactName ? quotation.contactName : '--' }}</span>
          </div>
        </div>

        <!-- Direct Email -->
        <div class="info-tile col-span-full">
          <div class="tile-badge badge-blue">
            <mat-icon>email</mat-icon>
          </div>
          <div class="info-content">
            <span class="tile-label">{{ 'email' | translate }}</span>
            <span class="tile-value text-sm flex items-center gap-1.5 font-mono">
              {{ quotation.email ? quotation.email : '--' }}
            </span>
          </div>
        </div>

        <!-- Email list -->
        <div class="info-tile col-span-full">
          <div class="tile-badge badge-purple">
            <mat-icon>alternate_email</mat-icon>
          </div>
          <div class="info-content">
            <span class="tile-label">{{ 'ADMIN.EMAIL_LIST' | translate }}</span>
            <div class="flex flex-wrap gap-1.5 mt-1">
              @if (quotation.contacts?.length) {
                @for (email of quotation.contacts; track email) {
                  <span class="px-2.5 py-0.5 rounded-md text-xs font-semibold font-mono email-badge">
                    {{ email }}
                  </span>
                }
              } @else {
                <span class="tile-value">--</span>
              }
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
